import SegmentedControl from 'components/inputs/segmented-control';
import PtacPreferencesField from 'components/structure-page/ptac-preferences-field';
import { useEffect, useState } from 'react';
import {
  PTAC_CONTROL_OPTIONS,
  PTAC_GRILLE_OPTIONS,
  PTAC_SLEEVE_CONDITION_OPTIONS,
  PTAC_SLEEVE_SIZE_OPTIONS,
  sleeveSizeAnswerOf
} from 'utils/ptac-preferences';
import { PtacExistingUnitsRecord } from '@alpinehomeair/common';

type PtacExistingUnitsFieldsProps = {
  existing: PtacExistingUnitsRecord;
  onChange: (patch: Partial<PtacExistingUnitsRecord>) => void;
};

export default function PtacExistingUnitsFields({
  existing,
  onChange
}: PtacExistingUnitsFieldsProps) {
  const [modelNumber, setModelNumber] = useState(existing.modelNumber ?? '');

  useEffect(() => {
    setModelNumber(existing.modelNumber ?? '');
  }, [existing.modelNumber]);

  const commitModelNumber = () => {
    const trimmed = modelNumber.trim();
    const next = trimmed.length > 0 ? trimmed : null;
    if (next !== (existing.modelNumber ?? null)) {
      onChange({ modelNumber: next });
    }
  };

  return (
    <div className='flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4'>
      <div className='flex flex-wrap gap-6'>
        <PtacPreferencesField
          label='Current sleeve size'
          hint='42 x 16 in. is the only chassis size sold; any other sleeve has to be replaced.'
        >
          <SegmentedControl
            label='Current sleeve size'
            options={PTAC_SLEEVE_SIZE_OPTIONS}
            value={sleeveSizeAnswerOf(existing)}
            onChange={(value) =>
              onChange({ sleeveStandard: value === 'standard' })
            }
          />
        </PtacPreferencesField>
        <PtacPreferencesField
          label='Sleeve condition'
          hint='Corroded sleeves or sleeves ten years and older should be replaced with the unit.'
        >
          <SegmentedControl
            label='Sleeve condition'
            options={PTAC_SLEEVE_CONDITION_OPTIONS}
            value={existing.sleeveCondition}
            onChange={(value) => onChange({ sleeveCondition: value })}
          />
        </PtacPreferencesField>
      </div>
      <div className='flex flex-wrap gap-6'>
        <PtacPreferencesField label='Exterior grille'>
          <SegmentedControl
            label='Exterior grille'
            options={PTAC_GRILLE_OPTIONS}
            value={existing.grille}
            onChange={(value) => onChange({ grille: value })}
          />
        </PtacPreferencesField>
        <PtacPreferencesField label='How are the units controlled today?'>
          <SegmentedControl
            label='How are the units controlled today?'
            options={PTAC_CONTROL_OPTIONS}
            value={existing.control}
            onChange={(value) => onChange({ control: value })}
          />
        </PtacPreferencesField>
      </div>
      <PtacPreferencesField
        label='Current model number (optional)'
        hint='From the rating plate behind the front cover.'
      >
        <input
          type='text'
          aria-label='Current model number'
          className='w-72 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 focus:border-blue-500 focus:outline-none'
          placeholder='e.g. PTC123G35AXXX'
          value={modelNumber}
          onChange={(event) => setModelNumber(event.target.value)}
          onBlur={commitModelNumber}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
        />
      </PtacPreferencesField>
    </div>
  );
}
